import { PageHead, Empty, Skel } from '../components/ui'
import { thaiDateTime } from '../lib/format'
import { useLiveFeed, type LiveEvent, type LiveKind, type LiveStatus } from '../lib/live'

const KIND_META: Record<LiveKind, { label: string; tone: string }> = {
  'finding.created': { label: 'ข้อสังเกตใหม่', tone: 'var(--color-amber)' },
  'case.transition': { label: 'เปลี่ยนสถานะเคส', tone: 'var(--color-gold)' },
  'job.failed': { label: 'งานล้มเหลว', tone: 'var(--color-crimson)' },
  'whistle.received': { label: 'แจ้งเบาะแสใหม่', tone: 'var(--color-emerald-soft)' },
}

const STATUS_META: Record<LiveStatus, { label: string; tone: string }> = {
  connecting: { label: 'กำลังเชื่อมต่อ', tone: 'var(--color-ivory-dim)' },
  live: { label: 'รับข้อมูลสด', tone: 'var(--color-emerald-soft)' },
  reconnecting: { label: 'กำลังเชื่อมต่อใหม่', tone: 'var(--color-amber)' },
  error: { label: 'เชื่อมต่อไม่ได้', tone: 'var(--color-crimson)' },
}

function text(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

function clip(value: string, max = 160): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value
}

/** One readable line per kind; the raw payload stays available in the tooltip. */
function summarize(event: LiveEvent): string {
  const p = event.payload
  const parts: string[] = []
  switch (event.kind) {
    case 'finding.created': {
      const title = text(p.title)
      if (title) parts.push(title)
      const severity = text(p.severity)
      if (severity) parts.push(`ระดับ ${severity}`)
      const rule = text(p.rule_code)
      if (rule) parts.push(`กฎ ${rule}`)
      break
    }
    case 'case.transition': {
      const from = text(p.from_status)
      const to = text(p.to_status)
      if (from || to) parts.push(`${from || '?'} → ${to || '?'}`)
      const caseNo = text(p.case_no)
      if (caseNo) parts.push(caseNo)
      break
    }
    case 'job.failed': {
      const job = text(p.job) || text(p.job_code) || text(p.kind)
      if (job) parts.push(job)
      const err = text(p.error) || text(p.message)
      if (err) parts.push(err)
      break
    }
    case 'whistle.received': {
      const category = text(p.category)
      if (category) parts.push(`หมวด ${category}`)
      const code = text(p.code) || text(p.tracking_code)
      if (code) parts.push(code)
      break
    }
  }
  if (parts.length === 0) {
    let added = 0
    for (const [key, value] of Object.entries(p)) {
      if (added >= 3) break
      if (key === 'tenant_id' || key === 'id') continue
      const rendered = text(value)
      if (rendered) {
        parts.push(`${key}=${rendered}`)
        added += 1
      }
    }
  }
  return clip(parts.join(' · ') || '—')
}

export function Live() {
  const { events, status, error } = useLiveFeed(50)
  const s = STATUS_META[status]

  return (
    <>
      <PageHead
        title="เหตุการณ์สด"
        sub={`${events.length} เหตุการณ์ล่าสุด · สตรีมหมุนทุก 30 วินาทีและกลับมาต่อจากเหตุการณ์ล่าสุดโดยไม่ซ้ำ`}
        actions={
          <span className="row" style={{ gap: 8 }} aria-live="polite">
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.tone }} />
            <span className="dim" style={{ fontSize: 12 }}>{s.label}</span>
          </span>
        }
      />

      {error ? (
        <div className="card card-pad" style={{ marginBottom: 16, borderColor: 'var(--color-crimson)' }}>
          <div className="row" style={{ gap: 10 }}>
            <span className="dim" style={{ fontSize: 12 }}>สตรีมขัดข้อง:</span>
            <span style={{ fontSize: 12.5 }}>{error}</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>กำลังลองใหม่ให้อัตโนมัติ</span>
          </div>
        </div>
      ) : null}

      {status === 'connecting' && events.length === 0 ? (
        <div className="stack"><Skel h={48} /><Skel h={48} /><Skel h={48} /><Skel h={48} /></div>
      ) : events.length === 0 ? (
        <div className="card">
          <Empty
            title="ยังไม่มีเหตุการณ์สด"
            body="เมื่อมีข้อสังเกต เคส งาน หรือเบาะแสใหม่ เหตุการณ์จะปรากฏที่นี่ภายในหนึ่งวินาที"
          />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 190 }}>เวลา</th>
                <th style={{ width: 190 }}>เหตุการณ์</th>
                <th>รายละเอียด</th>
                <th style={{ width: 90, textAlign: 'right' }}>ลำดับ</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => {
                const meta = KIND_META[event.kind]
                return (
                  <tr key={event.id} title={JSON.stringify(event.payload)}>
                    <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDateTime(event.created_at)}</td>
                    <td>
                      <span className="chip" style={{ color: meta.tone, borderColor: meta.tone }}>
                        {meta.label}
                      </span>
                    </td>
                    <td>{summarize(event)}</td>
                    <td className="mono dim" style={{ textAlign: 'right' }}>#{event.id}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
